import { ConfiguratorState } from '@/app/store/configurator/types/configurator.types';
import {
	BOTTOM_SHELF_DISTANCE,
	RACK_MATERIAL_COLORS,
	TOP_BRACE_MARGIN,
	TOP_SHELF_DISTANCE,
} from '@/shared/constants/configurator';
import { calculateRackDepth } from '@/shared/lib/configurator';
import { Section } from '@/shared/types/configurator';
import { BASE_SECTION_HEIGHT, MODEL_DISPLAY_SCALE } from '../constants';
import { BasePlate } from '../render-components/base-plate';
import { DiagonalBrace } from '../render-components/diagonal-brace';
import { HorizontalBrace } from '../render-components/horizontal-brace';
import { RenderComponentStrategy } from '../render-components/render-component.strategy';
import { UprightFrame } from '../render-components/upright-frame';
import {
	DiagonalBraceOptions,
	RenderComponentOptions,
	SectionOptions,
	Vector,
} from '../types/model.types';
import { ShelfBuilder } from './shelf.builder';

export class SectionBuilder {
	private components: RenderComponentStrategy[] = [];

	constructor(private readonly state: ConfiguratorState) {}

	build(options: SectionOptions) {
		this.components = [];

		this.addUprightFrames(options);
		this.addShelves(options);
		this.addBorders(options);

		if (options.side === 'back') {
			this.addWallConnectors(options);
		}

		const wallDepth = calculateRackDepth(this.state.sections);

		for (const component of this.components) {
			if (options.side === 'front') {
				const { position } = component.options;
				const zDiff = wallDepth - options.section.depth;

				component.setPosition([
					position[0],
					position[1],
					position[2] - zDiff * MODEL_DISPLAY_SCALE,
				]);
			}
		}

		return this.components;
	}

	private addUprightFrames({ section, index, position }: SectionOptions) {
		const frames: {
			position: Vector;
			rotation: Vector;
		}[] = [];

		const borderOptions = this.getBorderOptions(index);

		if (borderOptions.left) {
			frames.push(
				{
					position: [position.x, position.y, position.z],
					rotation: [Math.PI / 2, 0, 0],
				},
				{
					position: [
						position.x,
						position.y,
						position.z - section.depth * MODEL_DISPLAY_SCALE,
					],
					rotation: [Math.PI / 2, 0, 0],
				},
			);
		}

		if (borderOptions.right) {
			frames.push(
				{
					position: [
						position.x + section.width * MODEL_DISPLAY_SCALE,
						position.y,
						position.z,
					],
					rotation: [Math.PI / 2, 0, 0],
				},
				{
					position: [
						position.x + section.width * MODEL_DISPLAY_SCALE,
						position.y,
						position.z - section.depth * MODEL_DISPLAY_SCALE,
					],
					rotation: [Math.PI / 2, 0, 0],
				},
			);
		}

		for (const { position, rotation } of frames) {
			this.components.push(
				new UprightFrame({
					scale: [0.6, 0.6, 1],
					options: { height: section.height / 1000 },
					color: RACK_MATERIAL_COLORS[this.state.material],
					position,
					rotation,
				}),
			);

			this.components.push(
				new BasePlate({
					color: RACK_MATERIAL_COLORS[this.state.material],
					scale: [0.7, 0.7, 0.7],
					position: [
						position[0],
						position[1] - BASE_SECTION_HEIGHT * MODEL_DISPLAY_SCALE,
						position[2],
					],
					rotation: [Math.PI / 2, 0, 0],
				}),
			);
		}
	}

	private addShelves({ section, position }: SectionOptions) {
		const shelfEditableHeight = (section.height - BASE_SECTION_HEIGHT) * MODEL_DISPLAY_SCALE;
		const maxYValue = position.y + shelfEditableHeight;

		for (let i = 0; i < section.selfs.length; i++) {
			const shelf = section.selfs[i];
			const topMargin = this.calculateShelfTopMargin(section, i);

			const shelfComponent = new ShelfBuilder(this.state).build({
				position: {
					...position,
					y: maxYValue - topMargin * MODEL_DISPLAY_SCALE,
				},
				deck: shelf.deck,
				width: section.width,
				depth: section.depth,
			});

			this.components.push(...shelfComponent);
		}
	}

	private addBorders({ index, position, section, ...options }: SectionOptions) {
		const borderOptions = this.getBorderOptions(index);

		if (borderOptions.left) {
			this.addBorder({
				index,
				section,
				position,
				...options,
			});
		}

		if (borderOptions.right) {
			this.addBorder({
				index,
				section,
				position: {
					x: position.x + section.width * MODEL_DISPLAY_SCALE,
					y: position.y,
					z: position.z,
				},
				...options,
			});
		}
	}

	private addBorder({ position, section }: SectionOptions) {
		const horizontalBraceOptions: Omit<RenderComponentOptions<unknown>, 'position'> = {
			color: RACK_MATERIAL_COLORS[this.state.material],
			scale: [1, this.getHorizontalBraceDepthScale(section.depth), 1],
			rotation: [Math.PI / 2, 0, 0],
		};
		const horizontalBraceZPosition = position.z - (section.depth / 2) * MODEL_DISPLAY_SCALE;
		const shelfEditableHeight = section.height - BASE_SECTION_HEIGHT;

		const { blockComponents, topShelf } = this.getBorderCalculations(section);

		this.components.push(
			new HorizontalBrace({
				...horizontalBraceOptions,
				position: [
					position.x,
					position.y -
						(BASE_SECTION_HEIGHT - BOTTOM_SHELF_DISTANCE) * MODEL_DISPLAY_SCALE,
					horizontalBraceZPosition,
				],
			}),
		);

		if (topShelf) {
			this.components.push(
				new HorizontalBrace({
					...horizontalBraceOptions,
					position: [
						position.x,
						position.y + (shelfEditableHeight - TOP_BRACE_MARGIN) * MODEL_DISPLAY_SCALE,
						horizontalBraceZPosition,
					],
				}),
			);
		}

		for (let i = 0; i < blockComponents.length; i++) {
			const blockComponent = blockComponents[i];

			this.components.push(
				new HorizontalBrace({
					...horizontalBraceOptions,
					position: [
						position.x,
						(blockComponent.yPosition - BASE_SECTION_HEIGHT) * MODEL_DISPLAY_SCALE,
						horizontalBraceZPosition,
					],
				}),
			);
			this.addHorizontalBrace({
				squareDepth: section.depth,
				squareHeight: blockComponent.height - 10,
				side: i % 2 === 0 ? 'left' : 'right',
				position: [
					position.x,
					(blockComponent.yPosition - BASE_SECTION_HEIGHT - blockComponent.height * 0.5) *
						MODEL_DISPLAY_SCALE,
					horizontalBraceZPosition,
				],
			});
		}
	}

	private getBorderCalculations(section: Section) {
		const blockComponentsCount = Math.floor(section.height / 1000);
		const blockComponentsHeights =
			section.height % 1000 === 0
				? section.height - TOP_BRACE_MARGIN - BOTTOM_SHELF_DISTANCE
				: section.height - TOP_BRACE_MARGIN - BOTTOM_SHELF_DISTANCE - 500;

		const blockComponentHeight = blockComponentsHeights / blockComponentsCount;
		const blockComponents = Array.from({ length: blockComponentsCount }).map((_, index) => ({
			height: blockComponentHeight,
			yPosition: BOTTOM_SHELF_DISTANCE + (index + 1) * blockComponentHeight,
		}));

		return {
			blockComponents,
			topShelf: section.height % 1000 !== 0,
		};
	}

	private addHorizontalBrace({
		squareDepth,
		squareHeight,
		side,
		position,
	}: DiagonalBraceOptions) {
		const braceSize = Math.sqrt(Math.pow(squareDepth, 2) + Math.pow(squareHeight, 2));
		const baseRotation = this.getDiagonalBraceXRotation(squareDepth);
		const xRotation = side === 'left' ? baseRotation : -baseRotation;

		this.components.push(
			new DiagonalBrace({
				color: RACK_MATERIAL_COLORS[this.state.material],
				rotation: [xRotation, 0, 0],
				position,
				scale: [1, (braceSize / 0.73) * MODEL_DISPLAY_SCALE, 1],
			}),
		);
	}

	private addWallConnectors({ position, section }: SectionOptions) {
		const connectorDistance = section.height / 3;
		const editableSectionHeight = section.height - BASE_SECTION_HEIGHT;
		const zPosition = position.z + 100 * MODEL_DISPLAY_SCALE;

		const wallConnectors: Vector[] = [
			[
				position.x,
				position.y + (editableSectionHeight - connectorDistance) * MODEL_DISPLAY_SCALE,
				zPosition,
			],
			[
				position.x,
				position.y + (editableSectionHeight - connectorDistance * 2) * MODEL_DISPLAY_SCALE,
				zPosition,
			],
			[
				position.x + section.width * MODEL_DISPLAY_SCALE,
				position.y + (editableSectionHeight - connectorDistance) * MODEL_DISPLAY_SCALE,
				zPosition,
			],
			[
				position.x + section.width * MODEL_DISPLAY_SCALE,
				position.y + (editableSectionHeight - connectorDistance * 2) * MODEL_DISPLAY_SCALE,
				zPosition,
			],
		];

		for (const position of wallConnectors) {
			this.components.push(
				new HorizontalBrace({
					color: RACK_MATERIAL_COLORS[this.state.material],
					scale: [1, 0.13, 1],
					rotation: [Math.PI / 2, 0, 0],
					position,
				}),
			);
		}
	}

	private getHorizontalBraceDepthScale(shelfDepth: number) {
		const results: Record<number, number> = {
			500: 0.29,
			600: 0.35,
			700: 0.42,
			800: 0.48,
			900: 0.54,
		};

		return results[shelfDepth];
	}

	private getDiagonalBraceXRotation(shelfWidth: number) {
		const results: Record<number, number> = {
			500: Math.PI / 5.75,
			600: Math.PI / 4.75,
			700: Math.PI / 4.5,
			800: Math.PI / 4,
			900: Math.PI / 3.7,
		};

		return results[shelfWidth] || Math.PI / 4;
	}

	private calculateShelfTopMargin(section: Section, shelfIndex: number) {
		if (section.selfs.length !== 2) {
			const shelfDistance = section.height / (section.selfs.length + 0.5);
			return TOP_SHELF_DISTANCE + shelfDistance * shelfIndex;
		}

		const availableSectionHeight = section.height - 300;
		return (availableSectionHeight / 3) * (shelfIndex + 1);
	}

	private getBorderOptions(sectionIndex: number) {
		const currentSection = this.state.sections[sectionIndex];
		const prevSectionHeight = this.state.sections[sectionIndex - 1]?.height || 0;
		const nextSectionHeight = this.state.sections[sectionIndex + 1]?.height || 0;

		return {
			left: !this.state.wallMounted || prevSectionHeight <= currentSection.height,
			right:
				!this.state.wallMounted ||
				nextSectionHeight <= currentSection.height ||
				sectionIndex === this.state.sections.length - 1,
		};
	}
}
